/**
 * ContactListsPage.tsx
 *
 * Table page for viewing all contact lists. Row clicks navigate to the detail route.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, FileText, RefreshCw, ArrowLeft, Search, Trash2 } from 'lucide-react';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { FormField } from '@/components/ui/FormField';
import { Textarea } from '@/components/ui/Textarea';
import { FilterTabs } from '@/components/ui/FilterTabs';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useToast } from '@/hooks/useToast';
import { usePageContext } from '@/hooks/usePageContext';
import { getErrorMessage } from '@/services/apiClient';
import * as marketingSvc from '@/services/marketing.service';
import type { AudienceList } from '@/services/marketing.service';

export default function ContactListsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const updateAIContext = usePageContext('Contact Lists');

  useEffect(() => {
    updateAIContext({ dataSummary: 'Contact lists table page' });
  }, [updateAIContext]);

  const [search, setSearch] = useState('');
  const [listTypeFilter, setListTypeFilter] = useState('');
  // Inactive lists are hidden by default (SCRUM-1359): they are retired
  // audiences kept for history, and they crowd out the ones staff actually
  // send to.
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | ''>('active');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [createStep, setCreateStep] = useState<'type' | 'details'>('type');
  const [createType, setCreateType] = useState<'static' | 'dynamic'>('static');
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<AudienceList | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['marketing', 'lists', { search, page, statusFilter, listTypeFilter }],
    queryFn: () => marketingSvc.listAudienceLists({
      search,
      page,
      perPage: 25,
      // Status and type are filtered server-side, not on the fetched page: the
      // list is paginated, so filtering client-side would hide matches on other
      // pages and report a total that counts rows the table never shows.
      // `active` must be OMITTED for "All" — the API branches on has('active'),
      // so sending an empty value would read as 0 and show only inactive lists.
      ...(statusFilter === '' ? {} : { active: statusFilter === 'active' ? 1 : 0 } as const),
      ...(listTypeFilter === 'static' || listTypeFilter === 'dynamic' ? { listType: listTypeFilter } : {}),
    }),
  });

  const resetCreateModal = () => {
    setCreateOpen(false);
    setCreateStep('type');
    setCreateName('');
    setCreateDescription('');
  };

  const createMut = useMutation({
    mutationFn: (d: { name: string; description?: string; listType: 'static' | 'dynamic' }) =>
      marketingSvc.createAudienceList(d),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['marketing', 'lists'] });
      resetCreateModal();
      const newId = created?.emailListId ?? (created as unknown as { id?: number })?.id;
      if (newId) navigate(`/marketing/contact-lists/${newId}`);
    },
    onError: () => toast.error('Failed to create list'),
  });

  // Deleting a list is PERMANENT — email_lists has no soft-delete — and it also
  // drops the list's membership. Hence the confirm step, and the explicit
  // contact count in the prompt.
  const deleteMut = useMutation({
    mutationFn: (id: number) => marketingSvc.deleteAudienceList(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'lists'] });
      toast.success('List deleted');
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Failed to delete list')),
  });

  const lists = data?.data ?? [];
  const meta = data?.meta;

  const listTypeTabs = [
    { label: 'All', value: '' },
    { label: 'Static', value: 'static' },
    { label: 'Dynamic', value: 'dynamic' },
  ];

  // Active first, because that is the default view.
  const statusTabs = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
    { label: 'All', value: '' },
  ];

  const columns: DataTableColumn<AudienceList>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (r) => (
        <span className="font-medium text-slate-900">{r.name}</span>
      ),
    },
    {
      key: 'listType',
      header: 'Type',
      render: (r) => (
        <Badge variant={r.listType === 'dynamic' ? 'warning' : 'info'}>
          {r.listType === 'dynamic' ? 'Dynamic' : 'Static'}
        </Badge>
      ),
    },
    {
      key: 'active',
      header: 'Status',
      render: (r) => (
        <Badge variant={r.active ? 'success' : 'neutral'}>
          {r.active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'patientCount',
      header: 'Contacts',
      render: (r) => (r.patientCount ?? 0).toLocaleString(),
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (r) => r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '--',
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <div className="flex justify-end">
          <button
            type="button"
            aria-label={`Delete ${r.name}`}
            title="Delete list"
            // The row itself navigates to the detail page — without this the
            // click would open the list behind the confirm dialog.
            onClick={(e) => { e.stopPropagation(); setDeleteTarget(r); }}
            className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col h-full overflow-hidden p-6">
      <div className="flex-shrink-0 space-y-6">
        <PageHeader
          variant="compact"
          title="Contact Lists"
          actions={(
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCreateOpen(true)}>
              New List
            </Button>
          )}
        />

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <FilterTabs
              tabs={listTypeTabs}
              value={listTypeFilter}
              onChange={(v) => { setListTypeFilter(v); setPage(1); }}
            />
            <FilterTabs
              tabs={statusTabs}
              value={statusFilter}
              onChange={(v) => { setStatusFilter(v as 'active' | 'inactive' | ''); setPage(1); }}
            />
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search lists..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-9 w-64"
            />
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 mt-6">
        <DataTable
          keyField="emailListId"
          columns={columns}
          data={lists}
          loading={isLoading}
          onRowClick={(r) => navigate(`/marketing/contact-lists/${r.emailListId}`)}
          page={meta?.page ?? 1}
          pageSize={meta?.perPage ?? 25}
          total={meta?.total ?? 0}
          onPageChange={setPage}
          emptyTitle="No contact lists yet"
          emptyDescription="Create a list to organize your contacts."
        />
      </div>

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title="Delete contact list?"
        message={
          deleteTarget
            ? `Delete "${deleteTarget.name}"? This permanently removes the list and its ${(deleteTarget.patientCount ?? 0).toLocaleString()} contact${(deleteTarget.patientCount ?? 0) === 1 ? '' : 's'} from it. Campaigns already sent are not affected. This cannot be undone.`
            : ''
        }
        confirmLabel="Delete list"
        variant="danger"
        // ConfirmDialog drives its own spinner off this promise and calls
        // onClose itself once it settles; the catch keeps a failed delete from
        // surfacing as an unhandled rejection (the toast reports it).
        onConfirm={() =>
          deleteTarget
            ? deleteMut.mutateAsync(deleteTarget.emailListId).catch(() => {})
            : undefined
        }
        onClose={() => setDeleteTarget(null)}
      />

      <Modal isOpen={createOpen} onClose={resetCreateModal} title="New Contact List" size="md">
        {createStep === 'type' ? (
          <div className="flex flex-col items-center py-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-1">What type of list would you like to create?</h2>
            <p className="text-sm text-slate-500 mb-8">Choose the list type that best fits your needs.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full max-w-lg">
              <button
                type="button"
                onClick={() => { setCreateType('static'); setCreateStep('details'); }}
                className="border-2 border-slate-200 rounded-xl p-8 text-center hover:border-blue-400 hover:bg-slate-50 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer"
              >
                <FileText className="h-10 w-10 text-blue-500 mx-auto mb-4" />
                <div className="font-semibold text-base text-slate-800 mb-2">Static List</div>
                <div className="text-xs text-slate-500 leading-relaxed">
                  Manually add and remove contacts. Ideal for curated lists where you control exactly who is included.
                </div>
              </button>
              <button
                type="button"
                onClick={() => { setCreateType('dynamic'); setCreateStep('details'); }}
                className="border-2 border-slate-200 rounded-xl p-8 text-center hover:border-blue-400 hover:bg-slate-50 hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer"
              >
                <RefreshCw className="h-10 w-10 text-blue-500 mx-auto mb-4" />
                <div className="font-semibold text-base text-slate-800 mb-2">Dynamic List</div>
                <div className="text-xs text-slate-500 leading-relaxed">
                  Automatically populated based on filter criteria. The list updates itself whenever contacts match your selected filters.
                </div>
              </button>
            </div>
          </div>
        ) : (
          <div className="py-4 space-y-5">
            <button
              type="button"
              onClick={() => setCreateStep('type')}
              className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back
            </button>
            <div className="flex items-center gap-2 mb-2">
              {createType === 'static' ? (
                <FileText className="h-5 w-5 text-blue-500" />
              ) : (
                <RefreshCw className="h-5 w-5 text-blue-500" />
              )}
              <span className="text-sm font-medium text-slate-600">
                {createType === 'static' ? 'Static' : 'Dynamic'} List
              </span>
            </div>
            <FormField label="List Name" required>
              <Input
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="e.g., Newsletter Subscribers"
                autoFocus
              />
            </FormField>
            <FormField label="Description">
              <Textarea autoResize
                value={createDescription}
                onChange={(e) => setCreateDescription(e.target.value)}
                rows={3}
                placeholder="Brief description of this list's purpose"
              />
            </FormField>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={resetCreateModal}>
                Cancel
              </Button>
              <Button
                loading={createMut.isPending}
                disabled={!createName.trim()}
                onClick={() =>
                  createMut.mutate({
                    name: createName.trim(),
                    description: createDescription.trim() || undefined,
                    listType: createType,
                  })
                }
              >
                Create List
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
